import { useRef } from "react";
import {
  EditBase,
  Form,
  ShowBase,
  useRecordContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import {
  playableRecordings,
  type PlayerControl,
  RecordingPlayer,
} from "../zoom_meeting_summaries/RecordingPlayer";
import { EditContent } from "../zoom_meeting_summaries/ZoomMeetingSummaryEdit";
import { MeetingSaveProvider } from "../zoom_meeting_summaries/useSaveMeeting";
import { LeaveGuard } from "../zoom_meeting_summaries/LeaveGuard";
import type { ZoomMeetingSummaryView } from "../types";
import { MeetingParticipants } from "./MeetingParticipants";
import { MeetingTitle } from "./MeetingTitle";
import { useMeetingLinkTarget } from "./useMeetingLinkTarget";
import { useSeekWhenReady } from "../zoom_meeting_summaries/useSeekWhenReady";
import {
  type MeetingClock,
  meetingSpan,
  toMeetingTime,
} from "../zoom_meeting_summaries/meetingTimeline";
import { cn } from "@/lib/utils";
import { assetMessage, mediaState } from "./assetMessages";
import { MeetingAssetBar } from "./MeetingAssetBar";
import { useMeetingAssets } from "./useMeetingAssets";

// A meeting with recordings but no AI summary: show the video + participants.
function RecordingsOnlyMeeting({
  meeting,
}: {
  meeting: ZoomMeetingSummaryView;
}) {
  const videoRef = useRef<MeetingClock>(null);
  const meetingRef = meeting.id as number;
  const link = useMeetingLinkTarget();
  const translate = useTranslate();
  const [update] = useUpdate();
  const playerRef = useRef<PlayerControl | null>(null);
  // The view row and recordings, shared with the player's own query by key.
  const assets = useMeetingAssets(meetingRef);
  const hasPlayable = playableRecordings(assets.recordings).length > 0;
  const message = assetMessage("recording", mediaState(assets.row));
  // `t` is recording time; the player's clock takes meeting time.
  const leadIn = meetingSpan(meeting, assets.recordings).leadIn;
  useSeekWhenReady(
    videoRef,
    link.t == null ? null : toMeetingTime(link.t, leadIn),
    hasPlayable,
  );
  return (
    <div className="flex flex-col gap-4 p-4">
      <MeetingTitle
        record={meeting}
        // No review form on this page, so nothing unsaved to protect: write
        // straight through. LCCRM-88.
        onSave={async (title) => {
          await update(
            "zoom_meetings",
            {
              id: meeting.id,
              data: { custom_title: title },
              previousData: { id: meeting.id },
            },
            { returnPromise: true },
          );
        }}
      />
      <MeetingAssetBar
        meetingId={meetingRef}
        assets={assets}
        playerRef={playerRef}
        hasPanels={false}
      />
      <div
        className={cn(
          "h-[50vh] min-h-0",
          link.ringFor("screen", "camera", "audio"),
        )}
      >
        <RecordingPlayer
          videoRef={videoRef}
          meetingRef={meetingRef}
          meeting={meeting}
          initialKind={link.trackKind}
          controlRef={playerRef}
          emptyMessage={
            message &&
            translate(message.key, { asset: translate(message.args.asset) })
          }
        />
      </div>
      <div>
        <h3 className="font-semibold mb-2">Participants</h3>
        <MeetingParticipants meetingRef={meetingRef} />
      </div>
    </div>
  );
}

// Meetings with a summary reuse the full summary editor (bound to the meeting's
// summary_id); meetings with recordings but no summary get a lighter view.
const ShowRouter = () => {
  const meeting = useRecordContext<ZoomMeetingSummaryView>();
  if (!meeting) return null;

  if (meeting.status != null) {
    return (
      <EditBase
        resource="zoom_meetings"
        id={meeting.id}
        mutationMode="pessimistic"
        redirect={false}
      >
        {/* keepDirtyValues: see the note on the other mount site in
            ZoomMeetingSummaryEdit. Every write reconciles the cache, and a
            record change resets the form. */}
        {/* onSubmit no-op: see the note on the other mount site. Enter in a
            single-line input would otherwise submit the whole form outside the
            save queue. */}
        <Form
          resetOptions={{ keepDirtyValues: true }}
          onSubmit={() => undefined}
        >
          <MeetingSaveProvider>
            <LeaveGuard />
            <EditContent />
          </MeetingSaveProvider>
        </Form>
      </EditBase>
    );
  }

  return <RecordingsOnlyMeeting meeting={meeting} />;
};

export function ZoomMeetingShow() {
  return (
    <ShowBase>
      <ShowRouter />
    </ShowBase>
  );
}
